import { LocalStorage } from "@/common/storage.ts";
import { LANGUAGE_OPTION } from "@/constant/constants.ts";
import Properties from "@/constant/properties.ts";
import { COMMON_PATH, MANAGER_PATH } from "@/constant/url-path.ts";
import i18n from "@/i18n/i18n.ts";
import { toast } from "@/utils/toast.tsx";
import axios, {
  AxiosRequestConfig,
  AxiosResponse,
  CancelTokenSource,
} from "axios";

const axiosInstance = axios.create({
  baseURL: Properties.SERVER_LINK,
  withCredentials: true,
});

axiosInstance.interceptors.request.use(
  (config) => {
    config.headers["Accept-Language"] =
      LocalStorage.getLang() || LANGUAGE_OPTION.JAPAN;
    const accessToken = LocalStorage.getAccessToken();
    if (accessToken) {
      config.headers.Authorization = `Bearer ${accessToken}`;
    }
    if (config.data instanceof FormData) {
      config.headers["Content-Type"] = "multipart/form-data";
    } else {
      config.headers["Content-Type"] = "application/json";
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  },
);

export const GET = async <T>(
  url: string,
  config?: AxiosRequestConfig,
  isLoading: boolean = false,
  cancelToken?: CancelTokenSource,
): Promise<T | null | undefined> => {
  openLoading(isLoading);
  try {
    const response: AxiosResponse<T> = await axiosInstance.get(url, {
      ...config,
      cancelToken: cancelToken?.token,
    });
    return response.data;
  } catch (error) {
    handleError(error);
    throw error;
  } finally {
    closeLoading(isLoading);
  }
};

export const POST = async <T>(
  url: string,
  data: any,
  config?: AxiosRequestConfig,
  isLoading: boolean = false,
  cancelToken?: CancelTokenSource,
): Promise<T | null | undefined> => {
  openLoading(isLoading);
  try {
    const response: AxiosResponse<T> = await axiosInstance.post(url, data, {
      ...config,
      cancelToken: cancelToken?.token,
    });
    return response.data;
  } catch (error) {
    handleError(error);
    throw error;
  } finally {
    closeLoading(isLoading);
  }
};

export const PUT = async <T>(
  url: string,
  data: any,
  config?: AxiosRequestConfig,
  isLoading: boolean = false,
  cancelToken?: CancelTokenSource,
): Promise<T | null | undefined> => {
  openLoading(isLoading);
  try {
    const response: AxiosResponse<T> = await axiosInstance.put(url, data, {
      ...config,
      cancelToken: cancelToken?.token,
    });
    return response.data;
  } catch (error) {
    handleError(error);
    throw error;
  } finally {
    closeLoading(isLoading);
  }
};

export const DELETE = async <T>(
  url: string,
  config?: AxiosRequestConfig,
  isLoading: boolean = false,
  cancelToken?: CancelTokenSource,
): Promise<T | null | undefined> => {
  openLoading(isLoading);
  try {
    const response: AxiosResponse<T> = await axiosInstance.delete(url, {
      ...config,
      cancelToken: cancelToken?.token,
    });
    return response.data;
  } catch (error) {
    handleError(error);
    throw error;
  } finally {
    closeLoading(isLoading);
  }
};

export const DOWNLOAD_FILE = async (urlDownload: string, fileName: string) => {
  try {
    const response = await axiosInstance.get(urlDownload, {
      responseType: "blob",
      headers: {
        "Cache-Control": "no-cache, no-store, must-revalidate",
        Pragma: "no-cache",
        Expires: "0",
      },
      params: {
        _: new Date().getTime(), // Unique query parameter to prevent caching
      },
    });
    const url = window.URL.createObjectURL(new Blob([response.data]));
    const link = document.createElement("a");
    link.href = url;
    link.setAttribute("download", fileName);
    document.body.appendChild(link);
    link.click();
    window.URL.revokeObjectURL(url);
  } catch (error) {
    handleError(error);
  }
};

// Create function CancelTokenSource
export const createCancelToken = (): CancelTokenSource => {
  return axios.CancelToken.source();
};

const handleError = (error: any) => {
  if (axios.isCancel(error)) {
    console.error("Request canceled", error.message);
  } else {
    if (error?.code === "ERR_NETWORK") {
      toast.error(i18n.t("message.networkError"));
      return;
    }
    const status = error?.response?.status;
    const extractErrorContent = (data: any) => {
      let content = "";
      if (!data) return content;
      // HAL style: _embedded.errors[{message}]
      if (data._embedded && Array.isArray(data._embedded.errors)) {
        data._embedded.errors.forEach((e: any) => {
          if (e?.message) content += e.message + "\n";
        });
        if (content) return content;
      }
      // common shapes: messages[], message, messageError
      if (Array.isArray(data.messages) && data.messages.length > 0) {
        data.messages.forEach((value: any) => {
          content += (value.messageError || value.message || "") + "\n";
        });
        if (content) return content;
      }
      if (typeof data.message === "string" && data.message.trim())
        return data.message;
      if (typeof data.messageError === "string" && data.messageError.trim())
        return data.messageError;
      if (typeof data.error === "string" && data.error.trim())
        return data.error;
      // fallback: stringify minimal
      try {
        const s = JSON.stringify(data);
        if (s && s !== "{}") return s;
      } catch (e) {
        // ignore
      }
      return "";
    };

    if (status === 400 || status === 409 || status === 404) {
      const data: any = error?.response?.data;
      const contentError = extractErrorContent(data);
      toast.error(contentError || i18n.t("message.badRequest"));
    } else if (status === 401) {
      LocalStorage.clearAuthenticationStorage();
      window.location.href = MANAGER_PATH.AUTH.SIGN_IN;
    } else if (status === 403) {
      LocalStorage.clearAuthenticationStorage();
      window.location.href = COMMON_PATH.FORBIDDEN;
    } else if (status === 500) {
      const data: any = error?.response?.data;
      const contentError = extractErrorContent(data);
      toast.error(contentError || i18n.t("message.internalServerError"));
    }
  }
  throw error;
};

const openLoading = (show: boolean) => {
  if (!show) {
    return;
  }
  const loadingElement = document.getElementById("loading-global");
  if (loadingElement) {
    loadingElement.style.display = "grid";
  }
};

const closeLoading = (show: boolean) => {
  if (!show) {
    return;
  }
  const loadingElement = document.getElementById("loading-global");
  if (loadingElement) {
    loadingElement.style.display = "none";
  }
};

// Handel refresh token
// let isRefreshing = false;
// let refreshSubscribers: ((token: string) => void)[] = [];
//
// const subscribeTokenRefresh = (callback: (token: string) => void) => {
//     refreshSubscribers.push(callback);
// };
//
// const onRefreshed = (token: string) => {
//     refreshSubscribers.forEach(callback => callback(token));
//     refreshSubscribers = [];
// };

// axiosInstance.interceptors.response.use(
//     response => response,
//     async (error) => {
//         const originalRequest = error.config;
//         if (error.response?.status === 401 && !originalRequest._retry) {
//             if (isRefreshing) {
//                 // If the token is being refreshed, wait for the promise to resolve
//                 return new Promise((resolve) => {
//                     subscribeTokenRefresh((token: string) => {
//                         // Update original request with new token
//                         originalRequest.headers['Authorization'] = `Bearer ${token}`;
//                         resolve(axiosInstance(originalRequest));
//                     });
//                 });
//             }
//
//             isRefreshing = true;
//             originalRequest._retry = true;
//             const refreshToken = LocalStorage.getRefreshToken();
//             const username = LocalStorage.getUserInfo().username;
//             if (refreshToken) {
//                 const request: RefreshTokenRequest = {
//                     refreshToken: refreshToken,
//                     username: username,
//                 }
//                 LocalStorage.setAccessToken(null);
//                 LocalStorage.setExpiresDate(null);
//                 try {
//                     const res: RefreshTokenResponse | null | undefined = await AuthService.refreshTokenRequest(request);
//
//                     if (res?.accessToken) {
//                         LocalStorage.setAccessToken(res.accessToken);
//                         LocalStorage.setExpiresDate(res.expiresDate);
//                         axiosInstance.defaults.headers.common['Authorization'] = `Bearer ${res.accessToken}`;
//
//                         onRefreshed(res.accessToken);
//
//                         // Retry the original request with the new token
//                         return axiosInstance(originalRequest);
//                     }
//                 } catch (refreshError) {
//                     LocalStorage.clearAuthenticationStorage();
//                     window.location.href = COMMON_PATH.FORBIDDEN;
//                 } finally {
//                     isRefreshing = false;
//                 }
//             }
//         }
//
//         return Promise.reject(error);
//     }
// );
